import { useCallback, useEffect, type ReactNode } from "react";
import SmartMedia from "@/components/feature/SmartMedia";
import { isVideoUrl } from "@/lib/media";

interface ProductGalleryProps {
  images: string[];
  title: string;
  activeIndex: number;
  onIndexChange: (index: number) => void;
  /** Abre el visor a pantalla completa. */
  isFullscreen: boolean;
  onFullscreenChange: (open: boolean) => void;
  /** Contenido superpuesto sobre la imagen principal (badges, etc.). */
  overlay?: ReactNode;
}

/**
 * Galería de producto con navegación por flechas, miniaturas y un visor a
 * pantalla completa tipo carrusel (teclado: Esc, ← y →).
 */
export default function ProductGallery({
  images,
  title,
  activeIndex,
  onIndexChange,
  isFullscreen,
  onFullscreenChange,
  overlay,
}: ProductGalleryProps) {
  const total = images.length;

  const goPrev = useCallback(() => {
    if (total < 2) return;
    onIndexChange((activeIndex - 1 + total) % total);
  }, [activeIndex, total, onIndexChange]);

  const goNext = useCallback(() => {
    if (total < 2) return;
    onIndexChange((activeIndex + 1) % total);
  }, [activeIndex, total, onIndexChange]);

  // Navegación por teclado dentro del visor a pantalla completa.
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onFullscreenChange(false);
      if (event.key === "ArrowLeft") goPrev();
      if (event.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isFullscreen, goPrev, goNext, onFullscreenChange]);

  // Bloquea el scroll del fondo mientras el visor está abierto.
  useEffect(() => {
    if (!isFullscreen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isFullscreen]);

  const activeMedia = images[activeIndex] ?? images[0];

  return (
    <>
      <div>
        <div className="group relative aspect-square w-full overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
          <button
            type="button"
            onClick={() => activeMedia && onFullscreenChange(true)}
            aria-label="Ver imagen a pantalla completa"
            className="block h-full w-full cursor-zoom-in"
          >
            {activeMedia ? (
              <SmartMedia
                src={activeMedia}
                alt={title}
                priority
                className="h-full w-full object-cover object-top"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-foreground-300">
                <i className="ri-image-line text-5xl"></i>
              </span>
            )}
          </button>

          {overlay && (
            <div className="pointer-events-none absolute left-3 top-3 flex flex-col items-start gap-1.5">
              {overlay}
            </div>
          )}

          {/* Expansión a pantalla completa */}
          {activeMedia && (
            <button
              type="button"
              onClick={() => onFullscreenChange(true)}
              aria-label="Ver en pantalla completa"
              className="absolute right-3 top-3 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-200/70 bg-background-50/90 text-foreground-700 backdrop-blur transition-colors hover:bg-background-50"
            >
              <i className="ri-fullscreen-line text-lg"></i>
            </button>
          )}

          {total > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                aria-label="Imagen anterior"
                className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-200/70 bg-background-50/90 text-foreground-700 backdrop-blur transition-all hover:bg-background-50 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100"
              >
                <i className="ri-arrow-left-s-line text-2xl"></i>
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Imagen siguiente"
                className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-200/70 bg-background-50/90 text-foreground-700 backdrop-blur transition-all hover:bg-background-50 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100"
              >
                <i className="ri-arrow-right-s-line text-2xl"></i>
              </button>
              <span className="absolute bottom-3 right-3 rounded-full bg-background-950/70 px-2.5 py-1 text-xs font-medium text-white">
                {activeIndex + 1} / {total}
              </span>
            </>
          )}
        </div>

        {total > 1 && (
          <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
            {images.map((image, index) => {
              const video = isVideoUrl(image);
              return (
                <button
                  key={image}
                  type="button"
                  onClick={() => onIndexChange(index)}
                  aria-label={`Ver ${video ? "video" : "imagen"} ${index + 1}`}
                  className={`relative h-20 w-20 shrink-0 cursor-pointer overflow-hidden rounded-md border-2 transition-colors ${
                    activeIndex === index
                      ? "border-primary-400"
                      : "border-background-200 hover:border-background-300"
                  }`}
                >
                  {video ? (
                    <span className="flex h-full w-full items-center justify-center bg-background-800 dark:bg-background-200">
                      <i className="ri-play-circle-fill text-2xl text-white dark:text-foreground-950"></i>
                    </span>
                  ) : (
                    <SmartMedia
                      src={image}
                      alt={`${title} ${index + 1}`}
                      className="h-full w-full object-cover object-top"
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Visor a pantalla completa */}
      {isFullscreen && activeMedia && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-foreground-950/95 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={`Galería de ${title}`}
        >
          <div className="flex items-center justify-between px-4 py-3 md:px-6">
            <span className="rounded-full bg-background-50/10 px-3 py-1 text-sm font-medium text-white">
              {activeIndex + 1} / {total}
            </span>
            <button
              type="button"
              onClick={() => onFullscreenChange(false)}
              aria-label="Cerrar pantalla completa"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-background-50/10 text-white transition-colors hover:bg-background-50/20"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="relative flex flex-1 items-center justify-center px-4 pb-2 md:px-8">
            <div className="flex h-full max-h-[78vh] w-full max-w-5xl items-center justify-center">
              <SmartMedia
                src={activeMedia}
                alt={`${title} ${activeIndex + 1}`}
                priority
                className="max-h-[78vh] max-w-full rounded-lg object-contain"
              />
            </div>

            {total > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Imagen anterior"
                  className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/10 text-white transition-colors hover:bg-background-50/20 md:left-6"
                >
                  <i className="ri-arrow-left-s-line text-2xl"></i>
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Imagen siguiente"
                  className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/10 text-white transition-colors hover:bg-background-50/20 md:right-6"
                >
                  <i className="ri-arrow-right-s-line text-2xl"></i>
                </button>
              </>
            )}
          </div>

          {total > 1 && (
            <div className="flex justify-center gap-2 overflow-x-auto px-4 py-4">
              {images.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  onClick={() => onIndexChange(index)}
                  aria-label={`Ir al elemento ${index + 1}`}
                  className={`h-14 w-14 shrink-0 cursor-pointer overflow-hidden rounded-md border-2 transition-colors ${
                    activeIndex === index
                      ? "border-primary-400"
                      : "border-white/20 hover:border-white/40"
                  }`}
                >
                  {isVideoUrl(image) ? (
                    <span className="flex h-full w-full items-center justify-center bg-foreground-950/60">
                      <i className="ri-play-circle-fill text-xl text-white"></i>
                    </span>
                  ) : (
                    <SmartMedia
                      src={image}
                      alt={`${title} ${index + 1}`}
                      className="h-full w-full object-cover object-top"
                    />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}